/**
 * 炼器系统引擎
 * 与炼丹共用同样的经验曲线公式
 */

import type { PlayerState } from './Player';
import type { ForgeRecipe } from '../data/forgeRecipes';
import { FORGE_RECIPES, BASE_FORGE_RATE, FORGE_SKILL_BONUS } from '../data/forgeRecipes';
import { ITEMS } from '../data/items';

export interface ForgeResult {
  success: boolean;
  message: string;
  expGained: number;
  leveledUp: boolean;
  itemGained?: { id: string };
}

function expToLevel(level: number): number {
  return Math.floor(5 * Math.pow(level, 1.6));
}

export function getForgeLevel(exp: number): number {
  let lv = 0;
  while (expToLevel(lv + 1) <= exp) lv++;
  return lv;
}

export function getForgeProgress(exp: number): { level: number; current: number; needed: number; percent: number } {
  const level = getForgeLevel(exp);
  const prevCap = level > 0 ? expToLevel(level) : 0;
  const nextCap = expToLevel(level + 1);
  const current = exp - prevCap;
  const needed = nextCap - prevCap;
  return { level, current, needed, percent: needed > 0 ? Math.floor((current / needed) * 100) : 100 };
}

export function calcForgeRate(player: PlayerState): number {
  const exp = player.forgeExp ?? 0;
  const level = getForgeLevel(exp);
  return Math.min(0.95, Math.max(0.05, BASE_FORGE_RATE + level * FORGE_SKILL_BONUS));
}

export function canForge(player: PlayerState, recipe: ForgeRecipe): boolean {
  return getForgeLevel(player.forgeExp ?? 0) >= recipe.minSkill;
}

export function hasForgeIngredients(player: PlayerState, recipe: ForgeRecipe): string[] {
  const missing: string[] = [];
  for (const ing of recipe.ingredients) {
    const count = player.inventory.filter((i) => i.id === ing.itemId).length;
    if (count < ing.count) missing.push(`${ing.itemName}(${count}/${ing.count})`);
  }
  return missing;
}

function consumeForgeIngredients(player: PlayerState, recipe: ForgeRecipe): void {
  for (const ing of recipe.ingredients) {
    for (let i = 0; i < ing.count; i++) {
      const idx = player.inventory.findIndex((item) => item.id === ing.itemId);
      if (idx !== -1) player.inventory.splice(idx, 1);
    }
  }
}

export function performForge(player: PlayerState, recipe: ForgeRecipe): ForgeResult {
  const missing = hasForgeIngredients(player, recipe);
  if (missing.length > 0) {
    return { success: false, message: `材料不足：${missing.join('、')}`, expGained: 0, leveledUp: false };
  }
  if (!canForge(player, recipe)) {
    const lv = getForgeLevel(player.forgeExp ?? 0);
    return { success: false, message: `炼器等级不足！需要${recipe.minSkill}级，当前${lv}级`, expGained: 0, leveledUp: false };
  }

  consumeForgeIngredients(player, recipe);
  const rate = calcForgeRate(player);
  const oldLevel = getForgeLevel(player.forgeExp ?? 0);
  const expGain = Math.random() < rate ? 5 : 2;
  player.forgeExp = (player.forgeExp ?? 0) + expGain;
  const newLevel = getForgeLevel(player.forgeExp);

  if (Math.random() < rate) {
    const template = ITEMS.find((i) => i.id === recipe.resultItemId);
    if (template) {
      player.inventory.push(JSON.parse(JSON.stringify(template)));
    }
    const lvMsg = newLevel > oldLevel ? ` ⬆️炼器升至${newLevel}级！` : '';
    return { success: true, message: `🔨 炼器成功！获得 ${template?.name ?? recipe.resultItemId}！（成功率${(rate*100).toFixed(0)}%）${lvMsg}`, expGained: expGain, leveledUp: newLevel > oldLevel, itemGained: { id: recipe.resultItemId } };
  } else {
    const lvMsg = newLevel > oldLevel ? ` ⬆️炼器升至${newLevel}级！` : '';
    return { success: false, message: `💥 炼器失败！材料损毁...（成功率${(rate*100).toFixed(0)}%）${lvMsg}`, expGained: expGain, leveledUp: newLevel > oldLevel };
  }
}
